import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';
import upperCase from 'lodash/upperCase';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { compilePath } from '../../../../common/utils/helpers/routes';
import { sendEditSuggestion } from '../../../../common/services/api';
import { Logger } from '../../../../common/infrastructure/logger';
import {
  CustomError,
  ErrorType,
  useAppErrors,
} from '../../../../common/contexts/app-error-context';
import {
  clearFormState,
  clearRoutablePoints,
  currentFormIdChanged,
  formStateChanged,
  selectCurrentFormId,
  selectFormState,
} from '../../../../common/store/item-edit';
import {
  getLastSubmittedEmail,
  storeLastSubmittedEmail,
} from '../../../../common/utils/helpers/emailCache';
import {
  getItemMetricParams,
  getItemType,
} from '../../../../common/utils/helpers/item';
import { selectStyleSelectorShown } from '../../../../common/store/ui';
import { selectUserNotificationEnabled } from '../../../../common/store/user-notificaition-settings';
import { selectSearchParams } from '../../../../common/store/search';
import { enhanceEditSuggestoinPayload } from '../../../../common/utils/helpers/editSuggestions';
import { EDIT_SUGGESTION_VERSION } from '../../../../common/utils/constants/constants';
import {
  newPhotosHandler,
  initialLoadingPhotosHandler,
  photosReducer,
} from '../../../../common/components/controls/photo-upload/PhotoUpload';
import Form from '../../../../common/components/forms/form/Form';
import DefaultFormFooterHint from '../../../../common/components/forms/default-form-footer-hint/DefaultFormFooterHint';
import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import IconList, {
  IconListItem,
} from '../../../../common/components/icon-list/IconList';
import { FIELD_TYPES } from '../../../../common/components/forms/field/Field';
import { validators } from '../../../../common/utils/helpers/validators';
import { useMobile } from '../../../../common/components/responsive/Responsive';
import { IconName } from '../../../../common/components/icon/Icon';

import './CloseOrRemove.scss';

export const reasonOptions = [
  {
    id: 'permanentlyClosed',
    label: 'Permanently closed',
    icon: 'cross-circle-bold',
  },
  {
    id: 'doesntExistHere',
    label: "Doesn't exist here",
    icon: 'exclamation-mark-marker',
  },
  { id: 'other', label: 'Other', icon: 'ellipsis' },
];

const getFormFields = ({ attachmentsDispatch }) => [
  {
    field: 'email',
    label: 'Your email',
    defaultValue: '',
    getPrefillValue: () => getLastSubmittedEmail(),
    required: true,
    type: FIELD_TYPES.input,
    controlProps: {
      placeholder: 'You will get an email when changes are made',
      // name and autocomplete are required for email autocompletion done by browser
      name: 'email',
      autoComplete: 'email',
    },
    validate: validators.compose(
      validators.required('Email'),
      validators.email,
    ),
  },
  {
    field: 'comment',
    label: 'Comments',
    defaultValue: '',
    type: FIELD_TYPES.textArea,
    controlProps: {
      placeholder: 'Add here some additional information',
    },
  },
  {
    field: 'attachments',
    label: 'Photos',
    defaultValue: [],
    type: FIELD_TYPES.photos,
    controlProps: { photosDispatch: attachmentsDispatch },
  },
];

const FORM_ID = 'CloseOrRemoveForm';

interface CloseOrRemoveProps {
  className?;
  item;
  searchQuery;
  searchSelectedFeatureIndex;
  userLocation;
  onCancel;
  onSubmit;
}

interface RouteMatch {
  removeType;
}

const CloseOrRemove = ({
  className,
  item,
  searchQuery,
  searchSelectedFeatureIndex,
  userLocation,
  onCancel,
  onSubmit,
}: CloseOrRemoveProps) => {
  const {
    params: { removeType },
  } = useRouteMatch<RouteMatch>();
  const { showErrorPage } = useAppErrors();
  const [isLoading, setLoadingState] = useState(false);
  const notificationsEnabled = useSelector(selectUserNotificationEnabled);
  const searchParams = useSelector(selectSearchParams);
  const analyticsParams = getItemMetricParams(item, {
    query: searchParams.query,
  });

  const reason = useMemo(
    () =>
      removeType && reasonOptions.find((reason) => reason.id === removeType),
    [removeType],
  );

  const handleSubmit = useCallback(
    async (values: { email; comment; attachments: { fileId }[] }) => {
      const { email, comment, attachments } = values;
      const featureType = getItemType(item);

      try {
        const payload = pickBy(
          {
            version: EDIT_SUGGESTION_VERSION,
            action: 'remove',
            mapboxId: item.properties.mapbox_id,
            reason: snakeCase(reason.label),
            location: {
              longitude: item.geometry.coordinates[0],
              latitude: item.geometry.coordinates[1],
            },
            comment,
            attachments: Object.values(attachments)
              .filter(({ fileId }) => fileId)
              .map(({ fileId }) => fileId),
            userEmail: email,
            userLocation: !isEmpty(userLocation)
              ? {
                  longitude: userLocation.longitude,
                  latitude: userLocation.latitude,
                }
              : null,
            notificationsDisabled: !notificationsEnabled,
            searchQuery: searchQuery,
            selectedFeature: item ? JSON.stringify(item) : null,
            selectedFeatureIndex: searchSelectedFeatureIndex,
            placeName: item.properties.place_name,
          },
          (val) => !isNil(val),
        );

        enhanceEditSuggestoinPayload(payload, item);

        setLoadingState(true);

        const editSuggestionResult = await sendEditSuggestion(
          featureType,
          payload,
        );

        if (editSuggestionResult && editSuggestionResult.status === 'ok') {
          storeLastSubmittedEmail(email);

          if (onSubmit) {
            onSubmit(editSuggestionResult.result);
          }
        }

        analytics.track(
          `${upperCase(getItemType(item))} Closing Or Removal Submit: ${
            reason.label
          }`,
          analyticsParams,
        );
      } catch (error) {
        const _error: CustomError = error as CustomError;

        Logger.error('Error while submitting edit suggestion', _error.message);

        showErrorPage({
          type: ErrorType.Internal_API,
          message: _error.message,
        } as CustomError);
      } finally {
        setLoadingState(false);
      }
    },
    [
      item,
      reason,
      userLocation,
      notificationsEnabled,
      searchQuery,
      searchSelectedFeatureIndex,
      analyticsParams,
      onSubmit,
      showErrorPage,
    ],
  );

  const isMobile = useMobile();
  const [minHeight, setMinHeight] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();

  const layoutRef = useRef<HTMLElement>();
  useLayoutEffect(() => {
    if (!isMobile || reason) {
      setMinHeight(undefined);
      setMaxHeight(undefined);
      return;
    }

    if (layoutRef.current) {
      const layoutHeader: HTMLElement =
        layoutRef.current.getElementsByClassName(
          'wizard-layout__header',
        )[0] as HTMLElement;
      const layoutHeaderHeight = layoutHeader.offsetHeight;

      const reasons = layoutRef.current.getElementsByClassName(
        'close-or-remove__reasons-list',
      )[0];
      const reasonsHeight = reasons?.scrollHeight || 0;

      const firstReason: HTMLElement = reasons.getElementsByClassName(
        'icon-list__list-item',
      )[0] as HTMLElement;
      const firstReasonHeight = firstReason?.offsetHeight || 0;

      setMinHeight(layoutHeaderHeight + firstReasonHeight);
      setMaxHeight(layoutHeaderHeight + reasonsHeight);
    }
  }, [isMobile, reason]);

  const dispatch = useDispatch();
  const formState = useSelector(selectFormState);
  const currentFormId = useSelector(selectCurrentFormId);
  const formStateRef = useRef<Record<string, unknown>>();
  formStateRef.current = formState;
  const setFormState = useCallback(
    (formState) => dispatch(formStateChanged(formState)),
    [dispatch],
  );

  // clean up other forms to keep local storage size as small as possible
  useMount(() => {
    if (currentFormId !== FORM_ID) {
      dispatch(clearFormState());
      dispatch(currentFormIdChanged(FORM_ID));
    }

    dispatch(clearRoutablePoints());
  });

  const attachmentsDispatch = useCallback(
    (action) => {
      if (!formStateRef.current) {
        return;
      }

      const newAttachments = photosReducer(
        (formStateRef.current.values as Record<string, any>).attachments,
        action,
      );
      const newFormState = {
        ...formStateRef.current,
        values: {
          ...(formStateRef.current.values as Record<string, unknown>),
          attachments: newAttachments,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);

      return action;
    },
    [setFormState],
  );

  const attachments = useMemo(() => formState?.values.attachments, [formState]);

  // clear all still loading images, because it won't be handled anyway
  useMount(() => {
    if (attachments) {
      initialLoadingPhotosHandler(attachments, attachmentsDispatch);
    }
  });

  useEffect(() => {
    if (attachments) {
      newPhotosHandler(attachments, attachmentsDispatch);
    }
  }, [attachments, attachmentsDispatch]);

  const formFields = useMemo(
    () => getFormFields({ attachmentsDispatch }),
    [attachmentsDispatch],
  );

  const initialValues = useMemo(
    () =>
      formFields.reduce((acc, { field, defaultValue }) => {
        acc[field] = defaultValue;
        return acc;
      }, {}),
    [formFields],
  );

  useEffect(() => {
    if (!formState) {
      const values = formFields.reduce(
        (acc, { field, defaultValue, getPrefillValue }) => {
          acc[field] = (getPrefillValue && getPrefillValue()) || defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, formFields]);

  const history = useHistory();

  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  if (isMobile && styleSelectorShown) {
    return null;
  }

  if (reason) {
    return formState ? (
      <WizardLayout
        className={classnames('close-or-remove', className)}
        title={reason.label}
        hasTitleBackButton={isMobile}
        fullScreen={isMobile}
      >
        <Form
          state={formState}
          onStateChange={setFormState}
          className="close-or-remove__form"
          fields={formFields}
          initialValues={initialValues}
          formDisabled={isLoading}
          onCancel={onCancel}
          onSubmit={handleSubmit}
          footerHint={<DefaultFormFooterHint />}
        />
      </WizardLayout>
    ) : null;
  }

  return (
    <WizardLayout
      className={classnames('close-or-remove', className)}
      ref={layoutRef}
      title="Close or remove"
      description="Reason for suggested edit"
      hasOuterBackButton={isMobile}
      resizable={isMobile}
      resizeOptions={{ minHeight, maxHeight }}
      hasCloseButton={isMobile}
    >
      <IconList className="close-or-remove__reasons-list">
        {reasonOptions.map(({ id, label, icon }) => (
          <IconListItem
            key={id}
            icon={icon as IconName}
            component={Link}
            onClick={() =>
              analytics.track(`Open Close or Remove: ${label}`, analyticsParams)
            }
            to={{
              pathname: compilePath('remove', null, {
                removeType: id,
              }),
              search: history.location.search,
            }}
          >
            {label}
          </IconListItem>
        ))}
      </IconList>
    </WizardLayout>
  );
};

export default CloseOrRemove;
